import { MouseEvent, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Flex, Image, Text } from "rebass/styled-components";

import { VideoContainerFeed } from "../styledComponents";
import { countClipImpression, formatNumber } from "../../../Utilities/Clip";
import { formatRelativeTime } from "../../../Utilities/Time";
import { VideoPlayer } from "../../../VideoPlayer";
import { getAvatarImage } from "../../../Utilities/User";
import { IContentItem } from "../@types";
import { Link } from "../../../Components/Link";
import { ContentItemBadge, CONTENT_BADGES } from "../ContentItemBadge";
import styled from "styled-components";
import { TTags } from "MVVM/@types";
import { isTop100Clip } from "MVVM/Utilities/ClipTags";
import { analytics } from "MVVM/Analytics";
import { ClipContext } from "MVVM/Views/ClipExperience/@types";
import { Avatar } from "MVVM/Components/AvatarV2";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

interface IContentItemProps {
  isMontage?: boolean;
  isWrapped?: boolean;
  item: IContentItem;
  subtextColor?: string;
  title: string;
  borderColor?: string;
  navigationContext?: ClipContext;
  isProsFeed?: boolean;
  vanillaThumbnail?: boolean;
}

const getTagValue = (tags: TTags[] | undefined, key: string) =>
  tags?.find((tag: TTags) => tag.key === key)?.value;

const ContentItem = ({
  isMontage,
  item,
  subtextColor = "chalk",
  title,
  borderColor,
  navigationContext,
  vanillaThumbnail,
}: IContentItemProps) => {
  const history = useHistory();
  const [active, setActive] = useState(false);
  const [missingThumbnailChecked, setMissingThumbnailChecked] = useState(false);
  const thumbnail =
    item.thumbnailStandardUrl ||
    (item.clipImageThumb?.startsWith("http")
      ? item.clipImageThumb
      : `${REACT_APP_S3_CLIP_BUCKET}/${item.clipImageThumb}`);
  const thumbnailOg =
    item.thumbnailOverlayUrl ||
    `${REACT_APP_S3_CLIP_BUCKET}/${item.clipSnapshot}`;
  const primaryThumbnail = vanillaThumbnail ? thumbnail : thumbnailOg;
  const fallbackThumbnail = vanillaThumbnail ? thumbnailOg : thumbnail;
  const displayName = item.username || item.user?.username;
  const isInGamePlayerClip =
    Boolean(item.username) && item.username !== item.user?.username;
  const userProfileUrl =
    isInGamePlayerClip && item.playerGameIdentifier
      ? `/u/${item.playerGameIdentifier}`
      : `/u/${item.user?.username || item.username || item.user?._id}`;
  const clipLink = `/clip/${item.shareId}`;

  // Feeds set `badge` upstream (score / freshness). Fall back to Top 100 from
  // the clip's own tags so the grid tiles match the chip on every other surface.
  const badge =
    item.badge ??
    (isTop100Clip(item.tags) ? CONTENT_BADGES.TOP_100 : undefined);

  const playerName = getTagValue(item.tags, "PARTNER_playerName");
  const playerAvatar =
    getTagValue(item.tags, "PARTNER_avatar") ||
    getTagValue(item.tags, "PARTNER_playerPhoto");
  const isPro = Boolean(playerName);
  const proProfileUrl = item.playerGameIdentifier
    ? `/u/${item.playerGameIdentifier}`
    : userProfileUrl;

  const scrollToTop = () =>
    window.scroll({ top: 0, left: 0, behavior: "smooth" });

  // The clip page rebuilds context from the query string, so carry whatever
  // this feed was handed — pinned clips, for instance, scope the Recommended
  // rail to that user instead of the generic feed.
  const clipHref = (() => {
    if (!navigationContext) return clipLink;

    const params = new URLSearchParams({ context: navigationContext.type });
    const metadata = (navigationContext.metadata ?? {}) as Record<
      string,
      string | undefined
    >;
    (["userId", "tab", "playlistId", "startCursor"] as const).forEach((key) => {
      if (metadata[key]) params.set(key, metadata[key] as string);
    });

    return `${clipLink}?${params.toString()}`;
  })();

  const openClip = () => history.push(clipHref);

  const handleClick = (event?: MouseEvent) => {
    analytics.track("Homepage Feed - Click", {
      type: title,
    });

    // Open in new tab if ctrl/cmd click
    if (event?.ctrlKey || event?.metaKey) {
      window.open(clipHref, "_blank");
      return;
    }

    openClip();
  };

  return (
    <Flex
      flexDirection="column"
      style={{ height: "auto", width: "100%", position: "relative" }}
    >
      <ResponsiveContainer
        onMouseEnter={() => {
          if (!(item.previewUrl || item.clipPreviewPath)) return;

          setActive(true);
          countClipImpression(item.shareId, isMontage);
        }}
        onMouseLeave={() => setActive(false)}
        style={{
          border: borderColor ? `1px solid ${borderColor}` : "none",
          borderRadius: 9,
          overflow: "hidden",
        }}
      >
        {badge && <ContentItemBadge badge={badge} />}
        <Image
          src={primaryThumbnail}
          width="100%"
          style={{ position: "absolute", zIndex: 1, borderRadius: 9 }}
          onClick={(e: MouseEvent) => handleClick(e)}
          onError={(e) => {
            if (missingThumbnailChecked) return true;
            e.currentTarget.src = fallbackThumbnail;
            setMissingThumbnailChecked(true);
          }}
        />
        <VideoPreview
          active={active}
          item={item}
          title={title}
          handleClick={handleClick}
        />
      </ResponsiveContainer>
      <Flex mt={4} style={{ gap: 12 }}>
        <Avatar
          src={isPro ? playerAvatar : getAvatarImage(item.user)}
          size="sm"
          profileCardUsername={
            isPro ? undefined : item.user?.username || item.user?._id
          }
          userGroup={isPro ? "PRO_PLAYER" : item.user?.userGroup?.type}
          rank={item.user?.rank}
          link={isPro ? proProfileUrl : userProfileUrl}
        />
        <Box>
          <Text
            fontWeight={500}
            style={{ cursor: "pointer" }}
            onClick={(event: MouseEvent) => {
              analytics.track("Homepage Feed - Title Click", {
                type: title,
              });

              // Open in new tab if ctrl/cmd click
              if (event?.ctrlKey || event?.metaKey) {
                window.open(clipHref, "_blank");
                return;
              }

              openClip();
            }}
          >
            {item.title || item?.userClipTitle || item.clipTitle}
          </Text>
          {isPro ? (
            <>
              <Link to={proProfileUrl} onClick={scrollToTop}>
                <Text fontWeight={500} fontSize={1} color={subtextColor} mt={3}>
                  {playerName}
                </Text>
              </Link>
              <Link to={userProfileUrl} onClick={scrollToTop}>
                <ViaCreator>via HLTV</ViaCreator>
              </Link>
            </>
          ) : (
            <Link to={userProfileUrl} onClick={scrollToTop}>
              <Text fontWeight={500} fontSize={1} color={subtextColor} mt={3}>
                {displayName}
              </Text>
            </Link>
          )}
          <Text fontSize={1} color={subtextColor} fontWeight={500} mt={2}>
            {item.views >= 5 ? `${formatNumber(item.views)} views \u00b7 ` : ""}
            {formatRelativeTime(item.createdAt || "")}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

const VideoPreview = ({
  active,
  handleClick,
  item,
  title,
}: {
  active: boolean;
  handleClick: (event?: MouseEvent) => void;
  item: IContentItem;
  title: string;
}) => {
  const mp4src =
    item.previewUrl || `${REACT_APP_S3_CLIP_BUCKET}/${item.clipPreviewPath}`;
  const poster =
    item.thumbnailStandardUrl ||
    `${REACT_APP_S3_CLIP_BUCKET}/${item.clipImageThumb}`;
  const videoPlayerRef = useRef(null);

  return (
    <VideoContainerFeed
      clickable={true}
      onClick={(e: MouseEvent) => handleClick(e)}
      style={{
        transition: "opacity 0.5s ease-in-out",
        opacity: `${active ? 1 : 0}`,
      }}
    >
      {active && (
        <VideoPlayer
          refForVideoPlayer={videoPlayerRef}
          mp4Source={mp4src}
          hlsSource={item.hlsSrc}
          loop={true}
          playAnalyticsProperties={{
            shareId: item.shareId,
            clipId: item._id,
            clipOwner: item.user,
            total_views: item.views,
            game: item.game,
          }}
          watchAnalyticsProperties={{
            clipId: item._id,
            collection: title,
            collectionType: "Generic",
            item: item.shareId,
            itemType: "Clip",
            action: "preview",
          }}
          options={{
            controls: false,
            aspectRatio: "16:9",
            poster,
            playsinline: true,
            autoplay: "muted",
            userActions: {
              click: false,
            },
          }}
        />
      )}
    </VideoContainerFeed>
  );
};

const ResponsiveContainer = styled(Box)`
  aspect-ratio: 16/9;
  position: relative;
  width: 100%;
  cursor: pointer;
`;

const ViaCreator = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
  opacity: 0.75;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &:hover {
    opacity: 1;
  }
`;

export { ContentItem };
